/**
 * wholesale-create-draft.js
 *
 * Handles parsing order input (text/CSV/PDF), resolving SKUs via
 * the resolve-skus.php endpoint, displaying a confirmation table
 * with wholesale pricing, and submitting to create-draft.php.
 */

(function () {
  'use strict';

  const config = window.wholesaleCreateDraftConfig || {};
  if (!config.customerId || !config.customerEmail) return;

  // State
  let resolvedItems = [];
  let unresolvedItems = [];
  let ambiguousItems = [];
  let catalogItems = []; // full product catalog for combo search
  let originalInputText = '';
  let pendingFileData = null; // { base64, mimeType, fileName }

  // DOM Elements
  const el = {
    // Step containers
    stepInput: document.getElementById('wcd-step-input'),
    stepConfirm: document.getElementById('wcd-step-confirm'),

    // Input step
    inputText: document.getElementById('wcd-input-text'),
    fileInput: document.getElementById('wcd-file-input'),
    fileDrop: document.getElementById('wcd-file-drop'),
    parseBtn: document.getElementById('wcd-parse-btn'),
    inputFeedback: document.getElementById('wcd-input-feedback'),

    // Confirm step
    summary: document.getElementById('wcd-summary'),
    unmatchedSection: document.getElementById('wcd-unmatched'),
    unmatchedList: document.getElementById('wcd-unmatched-list'),
    ambiguousSection: document.getElementById('wcd-ambiguous'),
    ambiguousList: document.getElementById('wcd-ambiguous-list'),
    itemsBody: document.getElementById('wcd-items-body'),
    totals: document.getElementById('wcd-totals'),
    orderNote: document.getElementById('wcd-order-note'),
    addressSelect: document.getElementById('wcd-address-select'),
    submitBtn: document.getElementById('wcd-submit-btn'),
    backBtn: document.getElementById('wcd-back-btn'),
    confirmFeedback: document.getElementById('wcd-confirm-feedback')
  };

  // -----------------------------------------------------------------------
  // Bronze wholesale discount mapping
  // Must match wholesale-discount.liquid and wholesale-list.js
  // -----------------------------------------------------------------------
  const bronzeMapping = {
    20: 15,
    25: 20, 27: 20, 30: 20, 35: 20,
    32: 25, 40: 25,
    41: 30, 45: 30, 46: 30, 50: 30
  };

  const CACHE_KEY = 'wcd_resolve_cache';

  // -----------------------------------------------------------------------
  // Initialize
  // -----------------------------------------------------------------------
  function init() {
    setupEventListeners();
    populateAddressSelector();
    restoreFromCache();
  }

  function saveToCache() {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        resolved: resolvedItems,
        unresolved: unresolvedItems,
        ambiguous: ambiguousItems,
        catalog: catalogItems,
        inputText: originalInputText
      }));
    } catch (e) { /* storage full or unavailable */ }
  }

  function clearCache() {
    try { sessionStorage.removeItem(CACHE_KEY); } catch (e) {}
  }

  function restoreFromCache() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return;
      const cached = JSON.parse(raw);
      if (!cached.resolved || cached.resolved.length === 0) return;

      resolvedItems = cached.resolved;
      unresolvedItems = cached.unresolved || [];
      ambiguousItems = cached.ambiguous || [];
      catalogItems = cached.catalog || [];
      originalInputText = cached.inputText || '';

      showConfirmStep();
    } catch (e) { /* corrupt cache, ignore */ }
  }

  function setupEventListeners() {
    if (el.parseBtn) {
      el.parseBtn.addEventListener('click', handleParse);
    }

    if (el.fileInput) {
      el.fileInput.addEventListener('change', handleFileSelect);
    }

    // Drag and drop
    if (el.fileDrop) {
      el.fileDrop.addEventListener('dragover', (e) => {
        e.preventDefault();
        el.fileDrop.classList.add('dragover');
      });
      el.fileDrop.addEventListener('dragleave', () => {
        el.fileDrop.classList.remove('dragover');
      });
      el.fileDrop.addEventListener('drop', (e) => {
        e.preventDefault();
        el.fileDrop.classList.remove('dragover');
        if (e.dataTransfer.files.length > 0) {
          handleFile(e.dataTransfer.files[0]);
        }
      });
    }

    if (el.submitBtn) {
      el.submitBtn.addEventListener('click', handleSubmit);
    }

    if (el.backBtn) {
      el.backBtn.addEventListener('click', showInputStep);
    }
  }

  // -----------------------------------------------------------------------
  // Address Selector
  // -----------------------------------------------------------------------
  function populateAddressSelector() {
    if (!el.addressSelect) return;
    const customer = window.theme?.customer;
    if (!customer || !customer.addresses || customer.addresses.length === 0) return;

    customer.addresses.forEach((address, index) => {
      const option = document.createElement('option');
      option.value = index;
      option.dataset.addressData = JSON.stringify(address);

      const parts = [];
      if (address.first_name || address.last_name) {
        parts.push(`${address.first_name || ''} ${address.last_name || ''}`.trim());
      }
      if (address.address1) parts.push(address.address1);
      if (address.city) parts.push(address.city);
      if (address.province_code) parts.push(address.province_code);
      if (address.zip) parts.push(address.zip);

      option.textContent = parts.join(', ');
      if (address.default) option.selected = true;

      el.addressSelect.appendChild(option);
    });
  }

  function getSelectedAddress() {
    if (!el.addressSelect || el.addressSelect.value === '') return null;
    const option = el.addressSelect.options[el.addressSelect.selectedIndex];
    try {
      return JSON.parse(option.dataset.addressData);
    } catch (e) {
      return null;
    }
  }

  // -----------------------------------------------------------------------
  // File Handling
  // -----------------------------------------------------------------------
  function handleFileSelect(e) {
    if (e.target.files.length > 0) {
      handleFile(e.target.files[0]);
    }
  }

  async function handleFile(file) {
    const name = file.name.toLowerCase();
    const mimeTypes = {
      '.pdf': 'application/pdf',
      '.csv': 'text/csv',
      '.txt': 'text/plain',
      '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      '.xls': 'application/vnd.ms-excel'
    };

    const ext = Object.keys(mimeTypes).find(e => name.endsWith(e)) || '';
    const mimeType = mimeTypes[ext] || file.type || 'application/octet-stream';

    try {
      showFeedback(el.inputFeedback, `File loaded: ${file.name}`, 'success');

      // For text files (CSV/TXT), also show content in textarea
      if (mimeType === 'text/csv' || mimeType === 'text/plain') {
        const text = await readFileAsText(file);
        if (text && text.trim()) {
          el.inputText.value = text;
        }
      } else {
        el.inputText.value = `[File attached: ${file.name}]`;
        el.inputText.readOnly = true;
      }

      // Store base64 for sending to server
      const base64 = await readFileAsBase64(file);
      pendingFileData = { base64, mimeType, fileName: file.name };

    } catch (err) {
      console.error('File read error:', err);
      showFeedback(el.inputFeedback, 'Failed to read file: ' + err.message, 'error');
    }
  }

  function readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  function readFileAsBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Strip the data:...;base64, prefix
        const result = reader.result.split(',')[1];
        resolve(result);
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  // -----------------------------------------------------------------------
  // Resolve SKUs via PHP endpoint
  // -----------------------------------------------------------------------
  async function resolveItems(rawText, fileData) {
    const payload = {}; // the customer comes from the app proxy signature

    if (fileData) {
      payload.file_data = fileData.base64;
      payload.file_type = fileData.mimeType;
      payload.file_name = fileData.fileName;
    } else if (rawText) {
      payload.raw_text = rawText;
    }

    const response = await fetch(config.resolveEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const data = await response.json().catch(() => ({ error: `The wholesale server didn't answer properly (${response.status}). Please try again.` }));

    if (!response.ok || !data.success) {
      throw new Error(data.message || data.error || 'Failed to resolve products');
    }

    return data;
  }

  // -----------------------------------------------------------------------
  // Wholesale Discount Calculation
  // Copied from wholesale-list.js to maintain consistency
  // -----------------------------------------------------------------------
  function getWholesaleDiscountPercent(productTags) {
    if (!productTags || !Array.isArray(productTags)) return 0;

    const customer = window.theme?.customer;
    let isBronze = false;

    if (customer?.tags && Array.isArray(customer.tags)) {
      isBronze = customer.tags.some(tag => tag.toLowerCase() === 'bronzewholesale');
    }

    for (const tag of productTags) {
      const tagUpper = String(tag).toUpperCase();
      if (tagUpper.startsWith('WS')) {
        const valStr = tagUpper.substring(2);
        if (/^\d+$/.test(valStr)) {
          let val = parseInt(valStr, 10);
          if (val > 0 && val <= 100) {
            if (isBronze) {
              return bronzeMapping.hasOwnProperty(val) ? bronzeMapping[val] : 0;
            }
            return val;
          }
        }
      }
    }
    return 0;
  }

  function calculateWholesalePrice(priceCents, discountPercent) {
    if (discountPercent <= 0) return priceCents;
    const multiplier = (100 - discountPercent) / 100;
    return Math.round(priceCents * multiplier);
  }

  // -----------------------------------------------------------------------
  // Handle Parse & Match
  // -----------------------------------------------------------------------
  async function handleParse() {
    const rawText = el.inputText.value.trim();
    if (!rawText && !pendingFileData) {
      showFeedback(el.inputFeedback, 'Please enter some order data or upload a file.', 'error');
      return;
    }

    originalInputText = rawText;

    // Show loading
    el.parseBtn.disabled = true;
    showFeedback(el.inputFeedback, 'Using AI to parse and match your order...', 'loading');

    try {
      // Send file or raw text to server for AI parsing + SKU resolution
      const result = await resolveItems(
        pendingFileData ? null : rawText,
        pendingFileData
      );

      resolvedItems = result.resolved || [];
      unresolvedItems = result.unresolved || [];
      ambiguousItems = result.ambiguous || [];
      catalogItems = result.catalog || [];

      if (resolvedItems.length === 0 && ambiguousItems.length === 0) {
        showFeedback(el.inputFeedback, 'None of the entered items could be matched to products. Please check the SKUs and try again.', 'error');
        el.parseBtn.disabled = false;
        return;
      }

      // Enrich resolved items with discount data
      resolvedItems.forEach(item => {
        item.discountPercent = getWholesaleDiscountPercent(item.product_tags);
        item.priceCents = Math.round(parseFloat(item.price) * 100);
        item.wholesalePriceCents = calculateWholesalePrice(item.priceCents, item.discountPercent);
        item.editedQuantity = item.input_quantity;
      });

      hideFeedback(el.inputFeedback);
      saveToCache();
      showConfirmStep();

    } catch (err) {
      console.error('Resolve error:', err);
      showFeedback(el.inputFeedback, 'Failed to match products: ' + err.message, 'error');
    } finally {
      el.parseBtn.disabled = false;
    }
  }

  // -----------------------------------------------------------------------
  // Confirmation Step Rendering
  // -----------------------------------------------------------------------
  function showConfirmStep() {
    el.stepInput.classList.remove('active');
    el.stepConfirm.classList.add('active');

    renderSummary();
    renderUnmatched();
    renderAmbiguous();
    renderItemsTable();
    renderTotals();
  }

  function showInputStep() {
    el.stepConfirm.classList.remove('active');
    el.stepInput.classList.add('active');
    el.inputText.value = originalInputText;
    el.inputText.readOnly = false;
    pendingFileData = null;
    hideFeedback(el.confirmFeedback);
  }

  function renderSummary() {
    if (!el.summary) return;
    const total = resolvedItems.length + unresolvedItems.length + ambiguousItems.length;
    el.summary.innerHTML = `
      <div class="wcd-summary-stat">
        <span class="stat-value">${resolvedItems.length}</span>
        <span class="stat-label">matched</span>
      </div>
      <div class="wcd-summary-stat">
        <span class="stat-value">${ambiguousItems.length}</span>
        <span class="stat-label">need selection</span>
      </div>
      <div class="wcd-summary-stat">
        <span class="stat-value">${unresolvedItems.length}</span>
        <span class="stat-label">unmatched</span>
      </div>
      <div class="wcd-summary-stat">
        <span class="stat-value">${total}</span>
        <span class="stat-label">total input</span>
      </div>
    `;
  }

  function renderUnmatched() {
    if (!el.unmatchedSection || !el.unmatchedList) return;

    if (unresolvedItems.length === 0) {
      el.unmatchedSection.style.display = 'none';
      return;
    }

    el.unmatchedList.innerHTML = '';
    unresolvedItems.forEach((item, idx) => {
      const li = document.createElement('li');
      const inputLine = item.input_line || item.input_query || '';
      li.innerHTML = `
        <span>${escapeHtml(inputLine)} (qty: ${item.input_quantity})</span>
        <button class="wcd-resolve-unmatched-btn" data-unmatched-index="${idx}" title="Search for match">Search</button>
        <div class="wcd-combo-search wcd-unmatched-combo" data-unmatched-index="${idx}" style="display:none;">
          <input type="text" class="wcd-combo-input" placeholder="Search products..." autocomplete="off">
          <div class="wcd-combo-results"></div>
        </div>
      `;

      // Search button for unmatched items
      const searchBtn = li.querySelector('.wcd-resolve-unmatched-btn');
      const combo = li.querySelector('.wcd-combo-search');
      const input = li.querySelector('.wcd-combo-input');
      const results = li.querySelector('.wcd-combo-results');

      searchBtn.addEventListener('click', () => {
        const isVisible = combo.style.display !== 'none';
        combo.style.display = isVisible ? 'none' : 'block';
        if (!isVisible) {
          // Pre-fill search with the input query
          input.value = item.input_query || '';
          input.focus();
          renderComboResults(results, input.value, -1, item, idx);
        }
      });

      input.addEventListener('input', () => {
        renderComboResults(results, input.value, -1, item, idx);
      });

      el.unmatchedList.appendChild(li);
    });
    el.unmatchedSection.style.display = 'block';
  }

  function renderAmbiguous() {
    if (!el.ambiguousSection || !el.ambiguousList) return;

    if (ambiguousItems.length === 0) {
      el.ambiguousSection.style.display = 'none';
      return;
    }

    el.ambiguousList.innerHTML = '';
    ambiguousItems.forEach((item, index) => {
      const div = document.createElement('div');
      div.style.marginBlockEnd = 'var(--sp-4)';

      const label = document.createElement('label');
      label.style.fontWeight = 'var(--font-medium)';
      label.style.display = 'block';
      label.style.marginBlockEnd = 'var(--sp-2)';
      label.textContent = `"${item.input_query}" (qty: ${item.input_quantity}) - Select a match:`;

      const select = document.createElement('select');
      select.className = 'wcd-ambiguous-select';
      select.dataset.ambiguousIndex = index;

      const defaultOpt = document.createElement('option');
      defaultOpt.value = '';
      defaultOpt.textContent = 'Skip this item...';
      select.appendChild(defaultOpt);

      item.candidates.forEach((candidate, ci) => {
        const opt = document.createElement('option');
        opt.value = ci;
        opt.dataset.candidateData = JSON.stringify(candidate);
        const price = formatMoney(Math.round(parseFloat(candidate.price) * 100));
        opt.textContent = `${candidate.product_title} - ${candidate.variant_title} (SKU: ${candidate.sku || 'N/A'}) - ${price}`;
        select.appendChild(opt);
      });

      select.addEventListener('change', () => handleAmbiguousSelection(index, select));

      div.appendChild(label);
      div.appendChild(select);
      el.ambiguousList.appendChild(div);
    });

    el.ambiguousSection.style.display = 'block';
  }

  function handleAmbiguousSelection(ambiguousIndex, select) {
    const ambiguousItem = ambiguousItems[ambiguousIndex];

    // Remove any previous resolution for this ambiguous item
    resolvedItems = resolvedItems.filter(r => r._ambiguousIndex !== ambiguousIndex);

    if (select.value === '') return;

    const option = select.options[select.selectedIndex];
    try {
      const candidate = JSON.parse(option.dataset.candidateData);
      const resolved = {
        input_query: ambiguousItem.input_query,
        input_quantity: ambiguousItem.input_quantity,
        input_type: ambiguousItem.input_type,
        match: 'user_selected',
        variant_id: candidate.variant_id,
        sku: candidate.sku,
        product_title: candidate.product_title,
        variant_title: candidate.variant_title,
        price: candidate.price,
        product_tags: candidate.product_tags,
        available: candidate.available,
        _ambiguousIndex: ambiguousIndex,
        discountPercent: getWholesaleDiscountPercent(candidate.product_tags),
        priceCents: Math.round(parseFloat(candidate.price) * 100),
        editedQuantity: ambiguousItem.input_quantity
      };
      resolved.wholesalePriceCents = calculateWholesalePrice(resolved.priceCents, resolved.discountPercent);
      resolvedItems.push(resolved);
    } catch (e) {
      console.error('Failed to parse candidate:', e);
    }

    renderItemsTable();
    renderTotals();
  }

  function renderItemsTable() {
    if (!el.itemsBody) return;
    el.itemsBody.innerHTML = '';

    if (resolvedItems.length === 0) {
      const row = document.createElement('tr');
      row.innerHTML = '<td colspan="10" style="text-align: center; padding: var(--sp-8); color: rgb(var(--color-foreground) / 0.5);">No matched items yet</td>';
      el.itemsBody.appendChild(row);
      return;
    }

    resolvedItems.forEach((item, index) => {
      const row = document.createElement('tr');
      const hasDiscount = item.discountPercent > 0;
      const lineTotal = item.wholesalePriceCents * item.editedQuantity;
      const inputLine = item.input_line || item.input_query || '';

      const imgSrc = item.image_url || '';
      const imgHtml = imgSrc
        ? `<img src="${escapeHtml(imgSrc)}&width=80" alt="" class="wcd-item-img" loading="lazy">`
        : '<span class="wcd-item-img-placeholder"></span>';

      row.innerHTML = `
        <td class="wcd-img-cell">${imgHtml}</td>
        <td><code>${escapeHtml(item.sku || '-')}</code></td>
        <td>
          <span class="product-title">${escapeHtml(item.product_title)}</span>
          <button class="wcd-change-match-btn" data-item-index="${index}" title="Change match">&#x270E;</button>
          <div class="wcd-combo-search" data-item-index="${index}" style="display:none;">
            <input type="text" class="wcd-combo-input" placeholder="Search products..." autocomplete="off">
            <div class="wcd-combo-results"></div>
          </div>
        </td>
        <td><span class="variant-title">${escapeHtml(item.variant_title || '-')}</span>${item.available === false ? ' <span class="discount-badge" style="background:#c00;color:#fff;">Sold out</span>' : ''}</td>
        <td class="text-right">${formatMoney(item.priceCents)}</td>
        <td class="text-right">
          ${hasDiscount ? `<span class="price-discounted">${formatMoney(item.wholesalePriceCents)}</span>` : formatMoney(item.priceCents)}
        </td>
        <td class="text-center">
          ${hasDiscount ? `<span class="discount-badge">${item.discountPercent}%</span>` : '-'}
        </td>
        <td class="text-center">
          <input type="number" class="wcd-qty-input" value="${item.editedQuantity}" min="1" max="9999" data-item-index="${index}">
        </td>
        <td class="text-right"><strong>${formatMoney(lineTotal)}</strong></td>
        <td class="text-center">
          <button class="wcd-remove-btn" data-item-index="${index}" title="Remove item">&times;</button>
        </td>
      `;

      // Quantity change handler
      const qtyInput = row.querySelector('.wcd-qty-input');
      qtyInput.addEventListener('change', (e) => {
        const newQty = Math.max(1, parseInt(e.target.value, 10) || 1);
        e.target.value = newQty;
        resolvedItems[index].editedQuantity = newQty;
        renderItemsTable();
        renderTotals();
      });

      // Remove handler
      const removeBtn = row.querySelector('.wcd-remove-btn');
      removeBtn.addEventListener('click', () => {
        resolvedItems.splice(index, 1);
        renderItemsTable();
        renderTotals();
      });

      // Change match button handler
      const changeBtn = row.querySelector('.wcd-change-match-btn');
      const comboSearch = row.querySelector('.wcd-combo-search');
      const comboInput = row.querySelector('.wcd-combo-input');
      const comboResults = row.querySelector('.wcd-combo-results');

      changeBtn.addEventListener('click', () => {
        const isVisible = comboSearch.style.display !== 'none';
        comboSearch.style.display = isVisible ? 'none' : 'block';
        if (!isVisible) {
          comboInput.value = '';
          comboInput.focus();
          renderComboResults(comboResults, '', index, undefined, undefined);
        }
      });

      comboInput.addEventListener('input', () => {
        renderComboResults(comboResults, comboInput.value, index, undefined, undefined);
      });

      // Close combo on click outside
      document.addEventListener('click', (e) => {
        if (!comboSearch.contains(e.target) && e.target !== changeBtn) {
          comboSearch.style.display = 'none';
        }
      }, { once: false });

      el.itemsBody.appendChild(row);

      // Input line as a second row spanning Product + Variant
      if (inputLine) {
        const inputRow = document.createElement('tr');
        inputRow.className = 'wcd-input-row';
        inputRow.innerHTML = `
          <td colspan="10" class="wcd-input-line">${escapeHtml(inputLine)}</td>
        `;
        el.itemsBody.appendChild(inputRow);
      }
    });
  }

  function renderComboResults(container, query, itemIndex, unmatchedItem, unmatchedIdx) {
    container.innerHTML = '';
    const q = query.toLowerCase().trim();

    // Fuzzy term-split search: each term must match somewhere in title or SKU
    let matches = catalogItems;
    if (q.length > 0) {
      const terms = q.split(/\s+/).filter(t => t.length > 0);
      matches = catalogItems.filter(c => {
        const haystack = (c.title + ' ' + c.sku).toLowerCase();
        return terms.every(term => haystack.includes(term));
      });
    }
    matches = matches.slice(0, 20);

    if (matches.length === 0) {
      container.innerHTML = '<div class="wcd-combo-empty">No matching products</div>';
      return;
    }

    matches.forEach(cat => {
      const div = document.createElement('div');
      div.className = 'wcd-combo-item';
      div.innerHTML = `
        <span class="wcd-combo-sku">${escapeHtml(cat.sku || '-')}</span>
        <span class="wcd-combo-title">${escapeHtml(cat.title)}</span>
        <span class="wcd-combo-price">$${parseFloat(cat.ws_price).toFixed(2)}</span>
      `;
      div.addEventListener('click', () => {
        if (unmatchedItem && unmatchedIdx !== undefined) {
          // Resolve an unmatched item
          resolveUnmatchedItem(unmatchedIdx, unmatchedItem, cat);
        } else {
          selectComboItem(itemIndex, cat);
        }
      });
      container.appendChild(div);
    });
  }

  function resolveUnmatchedItem(unmatchedIdx, unmatchedItem, catalogEntry) {
    // Build a resolved item from the unmatched + catalog selection
    const titleParts = catalogEntry.title.split(' / ');
    const priceCents = Math.round(parseFloat(catalogEntry.rrp) * 100);
    const wsPriceCents = Math.round(parseFloat(catalogEntry.ws_price) * 100);
    let discountPercent = 0;
    if (priceCents > 0 && wsPriceCents < priceCents) {
      discountPercent = Math.round((1 - wsPriceCents / priceCents) * 100);
    }

    const newItem = {
      input_query: unmatchedItem.input_query,
      input_line: unmatchedItem.input_line || unmatchedItem.input_query || '',
      input_quantity: unmatchedItem.input_quantity,
      input_type: 'user_resolved',
      match: 'user_resolved',
      variant_id: catalogEntry.variant_id,
      sku: catalogEntry.sku,
      product_title: titleParts[0] || catalogEntry.title,
      variant_title: titleParts.length > 1 ? titleParts.slice(1).join(' / ') : 'Default Title',
      price: catalogEntry.rrp,
      priceCents: priceCents,
      discountPercent: discountPercent,
      wholesalePriceCents: wsPriceCents,
      editedQuantity: unmatchedItem.input_quantity
    };

    resolvedItems.push(newItem);
    unresolvedItems.splice(unmatchedIdx, 1);

    renderSummary();
    renderUnmatched();
    renderItemsTable();
    renderTotals();
  }

  function selectComboItem(itemIndex, catalogEntry) {
    const item = resolvedItems[itemIndex];
    if (!item) return;

    // Update the resolved item with the new match
    item.variant_id = catalogEntry.variant_id;
    item.sku = catalogEntry.sku;

    // Parse title into product + variant
    const titleParts = catalogEntry.title.split(' / ');
    item.product_title = titleParts[0] || catalogEntry.title;
    item.variant_title = titleParts.length > 1 ? titleParts.slice(1).join(' / ') : 'Default Title';

    item.priceCents = Math.round(parseFloat(catalogEntry.rrp) * 100);
    item.match = 'user_changed';

    // We don't have product_tags from catalog, so recalculate from prices
    // WS price is already in the catalog, derive discount from it
    const wsPriceCents = Math.round(parseFloat(catalogEntry.ws_price) * 100);
    if (item.priceCents > 0 && wsPriceCents < item.priceCents) {
      item.discountPercent = Math.round((1 - wsPriceCents / item.priceCents) * 100);
      item.wholesalePriceCents = wsPriceCents;
    } else {
      item.discountPercent = 0;
      item.wholesalePriceCents = item.priceCents;
    }

    renderItemsTable();
    renderTotals();
  }

  function renderTotals() {
    if (!el.totals) return;

    let subtotalRrp = 0;
    let subtotalWholesale = 0;

    resolvedItems.forEach(item => {
      subtotalRrp += item.priceCents * item.editedQuantity;
      subtotalWholesale += item.wholesalePriceCents * item.editedQuantity;
    });

    const totalDiscount = subtotalRrp - subtotalWholesale;
    const totalQty = resolvedItems.reduce((sum, item) => sum + item.editedQuantity, 0);

    el.totals.innerHTML = `
      <div class="totals-row">
        <span>Items:</span>
        <span>${totalQty}</span>
      </div>
      <div class="totals-row">
        <span>RRP Subtotal:</span>
        <span>${formatMoney(subtotalRrp)}</span>
      </div>
      <div class="totals-row">
        <span>Wholesale Discount:</span>
        <span>-${formatMoney(totalDiscount)}</span>
      </div>
      <div class="totals-row total">
        <span><strong>Estimated Total:</strong></span>
        <span>${formatMoney(subtotalWholesale)}</span>
      </div>
      ${minimumShortfall(subtotalWholesale) ? `<div class="wcd-feedback" data-state="error" style="display:block">${minimumShortfall(subtotalWholesale)}</div>` : ''}
    `;
    if (el.submitBtn) el.submitBtn.disabled = !!minimumShortfall(subtotalWholesale);
  }

  function wholesaleSubtotal() {
    return resolvedItems.reduce((sum, item) => sum + item.wholesalePriceCents * item.editedQuantity, 0);
  }

  function minimumShortfall(subtotal) {
    // the minimum lives on theme.customer (js-variables.liquid); AUD, converted for other currencies
    const min = Math.round((window.theme?.customer?.wholesaleMinimumCents || 0) * (Number(window.Shopify?.currency?.rate) || 1));
    if (!(subtotal < min)) return null;
    return `Minimum wholesale order ${formatMoney(min)} ${window.Shopify?.currency?.active || 'AUD'}`;
  }

  // -----------------------------------------------------------------------
  // Submit Order
  // -----------------------------------------------------------------------
  async function handleSubmit() {
    if (resolvedItems.length === 0) {
      showFeedback(el.confirmFeedback, 'No items to submit. Please go back and add items.', 'error');
      return;
    }

    const soldOut = resolvedItems.filter(i => i.available === false).map(i => i.product_title);
    if (soldOut.length) {
      showFeedback(el.confirmFeedback, 'These items are out of stock, please remove them: ' + soldOut.join(', '), 'error');
      return;
    }

    const shortfall = minimumShortfall(wholesaleSubtotal());
    if (shortfall) {
      showFeedback(el.confirmFeedback, shortfall, 'error');
      return;
    }

    const selectedAddress = getSelectedAddress();
    if (!selectedAddress) {
      showFeedback(el.confirmFeedback, 'Please select a shipping address.', 'error');
      return;
    }

    const customer = window.theme?.customer;
    if (!customer) {
      showFeedback(el.confirmFeedback, 'Customer data not available. Please refresh the page.', 'error');
      return;
    }

    el.submitBtn.disabled = true;
    el.submitBtn.textContent = 'Submitting...';
    hideFeedback(el.confirmFeedback);

    try {
      const payload = {
        cart: {
          items: resolvedItems.map(item => ({
            id: item.variant_id,
            quantity: item.editedQuantity
          })),
          note: el.orderNote ? el.orderNote.value : '',
          item_count: resolvedItems.reduce((sum, i) => sum + i.editedQuantity, 0)
        },
        shipping_address: selectedAddress,
        submitted_at: new Date().toISOString(),
        original_draft_id: null
      };

      const response = await fetch(config.createEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json().catch(() => ({ message: `The wholesale server didn't answer properly (${response.status}). Please try again.` }));

      if (!response.ok || result.error) {
        throw new Error(result.message || result.error || 'Failed to create draft order');
      }

      // Success
      clearCache();
      const orderName = result?.draft_order?.name || '';
      showSuccessModal(orderName);

    } catch (err) {
      console.error('Submit error:', err);
      let msg = err.message || 'Failed to create draft order. Please try again.';
      if (msg === 'Failed to fetch') {
        msg = 'Connection error: Could not reach the wholesale server. Please check your internet connection and try again.';
      }
      showFeedback(el.confirmFeedback, msg, 'error');
    } finally {
      el.submitBtn.disabled = false;
      el.submitBtn.textContent = 'Submit Wholesale Order';
    }
  }

  // -----------------------------------------------------------------------
  // Success Modal
  // -----------------------------------------------------------------------
  function showSuccessModal(orderName) {
    const overlay = document.createElement('div');
    overlay.className = 'wcd-success-overlay';

    const modal = document.createElement('div');
    modal.className = 'wcd-success-modal';

    const nameDisplay = orderName ? ` <strong>${escapeHtml(orderName)}</strong>` : '';

    modal.innerHTML = `
      <div style="font-size: 64px; margin-bottom: 20px;">&#10004;&#65039;</div>
      <h2 style="font-size: 28px; font-weight: 700; margin-bottom: 16px;">
        Order Created!
      </h2>
      <p style="font-size: 16px; color: rgb(var(--color-foreground) / 0.7); line-height: 1.6; margin-bottom: 24px;">
        Your wholesale order${nameDisplay} has been created successfully!
      </p>
      <p style="font-size: 14px; color: rgb(var(--color-foreground) / 0.5); margin-bottom: 24px;">
        A confirmation email has been sent with all the details.
      </p>
      <div style="display: flex; gap: 12px; flex-direction: column;">
        <button class="button button--primary" id="wcd-success-view-orders">
          View Your Orders
        </button>
        <button class="button button--secondary" id="wcd-success-new-order">
          Create Another Order
        </button>
      </div>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    document.getElementById('wcd-success-view-orders').addEventListener('click', () => {
      window.location.href = config.draftViewerUrl || '/pages/draft-order';
    });

    document.getElementById('wcd-success-new-order').addEventListener('click', () => {
      overlay.remove();
      clearCache();
      resolvedItems = [];
      unresolvedItems = [];
      ambiguousItems = [];
      catalogItems = [];
      originalInputText = '';
      pendingFileData = null;
      if (el.inputText) { el.inputText.value = ''; el.inputText.readOnly = false; }
      if (el.orderNote) el.orderNote.value = '';
      hideFeedback(el.confirmFeedback);
      hideFeedback(el.inputFeedback);
      showInputStep();
    });
  }

  // -----------------------------------------------------------------------
  // Utility Functions
  // -----------------------------------------------------------------------
  function formatMoney(cents) {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(dollars);
  }

  function escapeHtml(text) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return text ? String(text).replace(/[&<>"']/g, m => map[m]) : '';
  }

  function showFeedback(node, message, state) {
    if (!node) return;
    if (state === 'loading') {
      node.innerHTML = `<span class="wcd-spinner"></span> ${escapeHtml(message)}`;
    } else {
      node.textContent = message;
    }
    node.style.display = 'block';
    node.dataset.state = state || '';
  }

  function hideFeedback(node) {
    if (!node) return;
    node.style.display = 'none';
    node.textContent = '';
    delete node.dataset.state;
  }

  // Expose cache helpers for console use
  window.wcdSaveCache = saveToCache;
  window.wcdClearCache = clearCache;

  // -----------------------------------------------------------------------
  // Init on DOM ready
  // -----------------------------------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
